<template>
  <div class="search-page">
    <div
      class="filter-container"
      :class="{expanded: filterOptionsExpanded}"
    >
      <div 
        class="filter-container-header"
        @click="filterOptionsExpanded = !filterOptionsExpanded"
      >
        Filter Results

        <span :class="filterOptionsExpanded ? 'up-arrow' : 'down-arrow'" />
      </div>
      <div
        class="filter-container-content"
        :class="{
          expanded: filterOptionsExpanded,
          collapsed: !filterOptionsExpanded,
        }"
      >
        <div class="input-group">
          <TextInput
            v-model="searchTerm"
            label="Search Term"
          />
          <Dropdown
            v-model="buildStepId"
            label="Build Step"
            :options="buildStepDropdownOptions"
          />
        </div>
        <div class="button-wrapper">
          <button
            class="clear"
            @click="clearSearchForm"
          >
            Clear
          </button>
          <button
            class="search"
            @click="search"
          >
            Search
          </button>
        </div>
      </div>
    </div>
    <div class="results-container-header">
      <div class="results-container-header-text">
        Services
      </div>
      <router-link
        class="create"
        to="/service/0"
      >
        Create
      </router-link>
    </div>
    <LoadingPulse
      :loading="loading"
      class="loading-spinner"
    />
    <div
      v-if="!loading"
      class="overflow-wrapper"
    >
      <table>
        <thead>
          <tr>
            <th @click="sortBy('Name')">
              <div>
                Name
                <span
                  v-show="sortField === 'Name'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('Price')">
              <div>
                Price
                <span
                  v-show="sortField === 'Price'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th @click="sortBy('BuildStepName')">
              <div>
                Build Step
                <span
                  v-show="sortField === 'BuildStepName'"
                  :class="ascending ? 'up-arrow' : 'down-arrow'"
                />
              </div>
            </th>
            <th class="no-sort" />
          </tr>
        </thead>
        <tbody v-if="services.length > 0">
          <tr
            v-for="service in services"
            :key="service.id"
            class="clickable"
            @click="onTableRowClicked(service.id)"
          >
            <td>{{ service.name }}</td>
            <td>{{ $methods.formatPrice(service.price) }}</td>
            <td>{{ service.buildStepName }}</td>
            <td class="delete">
              <TrashCanSvg @click.stop="onDeleteClicked(service.id)" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td
              :colspan="4"
              class="message"
            >
              No services were found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Paginator
      v-if="!loading"
      class="paginator"
      :total-results="totalResults"
      :page-number="pageNumber"
      :page-size="pageSize"
      @page-change="onPageChanged"
      @page-size-change="onPageSizeChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import Paginator from '@/components/shared/Paginator.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import { ServiceService } from '@/services/service.service'
import { Service } from '@/@types/responses/service/Service'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { BuildStepService } from '@/services/buildStep.service'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Services',
  components: {
    Dropdown,
    LoadingPulse,
    Paginator,
    TextInput,
    TrashCanSvg
  },
  setup() {
    const buildStepService = inject(PROVIDER_CONSTANTS.buildStepService) as BuildStepService
    const serviceService = inject(PROVIDER_CONSTANTS.serviceService) as ServiceService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildStepService,
      serviceService,
      toast
    }
  },
  data() {
    let buildStepId: number|undefined

    return {
      ascending: true,
      buildStepDropdownOptions: [] as DropdownOptionResponse<number>[],
      buildStepId,
      filterOptionsExpanded: true,
      loading: true,
      pageNumber: 1,
      pageSize: 10,
      searchTerm: '',
      services: [] as Service[],
      sortField: 'Name',
      totalResults: 0
    }
  },
  mounted() {
    window.addEventListener("popstate", this.parseQueryStringAndSearch)
    
    this.fetchBuildStepDropdownOptions().then(() => {
      this.parseQueryStringAndSearch()
    })
  },
  unmounted() {
    window.removeEventListener('popstate', this.parseQueryStringAndSearch)
  },
  methods: {
    clearSearchForm(): void{
      this.searchTerm = ''
      this.buildStepId = undefined
      this.search()
    },
    fetchBuildStepDropdownOptions(): Promise<void> {
      return this.buildStepService.getBuildStepDropdownOptions().then(dropdownOptionsResponse => {
        this.buildStepDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build steps. Try reloading the page.')
        }
      })
    },
    getBooleanParameterByName(name: string): boolean | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? parameter === 'true' : undefined
    },
    getNumericParameterByName(name: string): number | undefined {
      const parameter = this.getStringParameterByName(name)
      return parameter !== undefined ? Number(parameter) : undefined
    },
    getStringParameterByName(name: string): string | undefined {
      const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)")
      const results = regex.exec(window.location.href)

      if (!results || !results[2])
        return undefined
      
      return decodeURIComponent(results[2].replace(/\+/g, " "));
    },
    onDeleteClicked(id: number): void {
      this.serviceService.deleteService(id).then(() => {
        if ((this.services.length - 1) === 0 && this.pageNumber > 1)
          this.onPageChanged(this.pageNumber - 1)
        else
          this.searchServices()

        this.toast.success('Service deleted successfully.')
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the service. Please try again.')
        }
      })
    },
    onPageChanged(pageNumber: number): void {
      this.pageNumber = pageNumber
      this.searchServices()
    },
    onPageSizeChanged(pageSize: number): void {
      this.pageSize = pageSize
      this.search()
    },
    onTableRowClicked(id: number): void {
      this.$router.push('/service/' + id)
    },
    parseQueryStringAndSearch(): void {
      const ascendingFromUrl = this.getBooleanParameterByName("a")
      this.ascending = ascendingFromUrl !== undefined ? ascendingFromUrl : this.ascending

      const buildStepFromUrl = this.getNumericParameterByName("b")
      this.buildStepId = buildStepFromUrl !== undefined ? buildStepFromUrl : this.buildStepId

      const pageNumberFromUrl = this.getNumericParameterByName("pn")
      this.pageNumber = pageNumberFromUrl !== undefined ? pageNumberFromUrl : this.pageNumber

      const pageSizeFromUrl = this.getNumericParameterByName("ps")
      this.pageSize = pageSizeFromUrl !== undefined ? pageSizeFromUrl : this.pageSize

      const searchTermFromUrl = this.getStringParameterByName("st")
      this.searchTerm = searchTermFromUrl !== undefined ? searchTermFromUrl : this.searchTerm

      const sortFieldFromUrl = this.getStringParameterByName("sf")
      this.sortField = sortFieldFromUrl !== undefined ? sortFieldFromUrl : this.sortField

      this.searchServices()
    },
    search(): void {
      this.pageNumber = 1
      this.searchServices()
    },
    searchServices(): void {
      this.loading = true
      this.updateUrl()
      this.serviceService.searchServices(this.searchTerm, this.pageNumber, this.pageSize, this.sortField, this.buildStepId, this.ascending).then(pagingResponse => {
        this.totalResults = pagingResponse.totalResults
        this.services = pagingResponse.results
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the services. Try reloading the page.')
        }
      })
    },
    sortBy(sortField: string): void {
      if (this.sortField === sortField)
        this.ascending = !this.ascending
      else
        this.sortField = sortField

      this.searchServices()
    },
    updateUrl(): void {
      let newQueryString = `?a=${this.ascending}&pn=${this.pageNumber}&ps=${this.pageSize}&sf=${this.sortField}`

      if (this.buildStepId !== undefined)
        newQueryString = newQueryString + `&b=${this.buildStepId}`

      if (this.searchTerm !== '')
        newQueryString = newQueryString + `&st=${encodeURIComponent(this.searchTerm)}`
      
      this.$router.replace('/services' + newQueryString)
    }
  }
})
</script>

<style scoped lang="scss">
.search-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 15px;

  @include sac-search-page;
}
</style>
